/*
  Neroli Theme — cart-v2.js
  Handles: intercepting product-form submissions to add via AJAX, opening the
  cart drawer, quantity +/- and remove within the drawer/cart page, and
  keeping the header cart count in sync. Vanilla JS, no dependencies.
*/

// Pending debounced quantity requests, keyed by line item. Declared up front
// so CartDrawer.refresh() can check it safely.
const cartQuantityTimers = new Map();

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-cart-drawer-body]');
    this.sectionId = this.dataset.sectionId;

    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-open]')) {
        event.preventDefault();
        this.open();
      }
      if (event.target.closest('[data-cart-close]') && this.contains(event.target)) {
        this.close();
      }
      if (event.target === this.querySelector('.cart-drawer__overlay')) {
        this.close();
      }
    });

    document.addEventListener('cart:updated', () => this.refresh());
  }

  open() {
    this.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.hidden = true;
    document.body.style.overflow = '';
  }

  async refresh() {
    // A quantity change is still settling; refreshing now would replace the
    // inputs with server values that are already out of date.
    if (cartQuantityTimers.size > 0) return;

    try {
      // The footer is replaced wholesale below, which would wipe a note the
      // customer is part-way through typing. Capture it first and restore it.
      const liveNote = this.querySelector('[data-cart-note]');
      const noteValue = liveNote ? liveNote.value : null;
      const noteWasOpen = liveNote ? !!liveNote.closest('details[open]') : false;
      const noteHadFocus = liveNote ? document.activeElement === liveNote : false;

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart?section_id=${this.sectionId}`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const newBody = doc.querySelector('[data-cart-drawer-body]');
      const newFooter = doc.querySelector('.cart-drawer__footer');
      const currentFooter = this.querySelector('.cart-drawer__footer');

      if (newBody) this.body.innerHTML = newBody.innerHTML;
      if (currentFooter && newFooter) {
        currentFooter.outerHTML = newFooter.outerHTML;
      } else if (!currentFooter && newFooter) {
        this.querySelector('.cart-drawer__panel').insertAdjacentHTML('beforeend', newFooter.outerHTML);
      } else if (currentFooter && !newFooter) {
        currentFooter.remove();
      }

      if (noteValue !== null) {
        const restoredNote = this.querySelector('[data-cart-note]');
        if (restoredNote) {
          restoredNote.value = noteValue;
          const details = restoredNote.closest('details');
          if (details && noteWasOpen) details.open = true;
          if (noteHadFocus) restoredNote.focus();
        }
      }
    } catch (error) {
      window.location.reload();
    }
  }
}
customElements.define('cart-drawer', CartDrawer);

async function cartRequest(url, body) {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    // /cart/change.js and /cart/update.js both return the whole cart, so the
    // count comes straight from this response rather than a second request.
    paintCartCount(data);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    return data;
  } catch (error) {
    // The section won't re-render, so clear any spinners we left spinning and
    // fall back to a reload rather than stranding the customer.
    document
      .querySelectorAll('[data-cart-item][data-updating]')
      .forEach((row) => row.removeAttribute('data-updating'));
    window.location.reload();
    return null;
  }
}

// Paint the header count from a cart object we already have. Refetching
// /cart.js separately raced the change request and could paint a stale count.
function paintCartCount(cart) {
  if (!cart || typeof cart.item_count !== 'number') return;
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = cart.item_count;
    el.hidden = cart.item_count === 0;
  });
  document.querySelectorAll('[data-cart-count-label]').forEach((el) => {
    el.textContent = '(' + cart.item_count + ')';
  });
}

async function updateCartCount() {
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
    paintCartCount(await response.json());
  } catch (error) {
    /* fail silently, count just won't update */
  }
}

// Intercept product form submissions -> add via AJAX, then open the drawer
document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-product-form]');
  if (!form) return;

  /* Shopify's accelerated checkout buttons live inside this same form and
     submit it to go to checkout. Intercepting every submit meant Buy it now
     was cancelled and silently turned into an add-to-cart, so the customer
     pressed it and nothing happened. Only our own add button is handled here;
     anything else is left to submit normally.

     event.submitter is undefined for a programmatic form.submit(), which
     nothing in the theme does, so treating that as our own add is safe. */
  const submitter = event.submitter;
  if (submitter && !submitter.hasAttribute('data-add-to-cart')) return;

  event.preventDefault();

  const submitButton = form.querySelector('[data-add-to-cart]');
  // Remember the button's normal label so we can restore it, and drive visual
  // state through classes/attributes rather than leaving it looking inert.
  const labelEl = submitButton?.querySelector('[data-add-to-cart-text]') || submitButton;
  const originalLabel = labelEl ? labelEl.textContent : '';
  if (submitButton) {
    submitButton.disabled = true;
    submitButton.setAttribute('aria-busy', 'true');
    submitButton.classList.add('is-loading');
    submitButton.classList.remove('is-added');
  }

  const formData = new FormData(form);
  const payload = Object.fromEntries(formData.entries());

  let errorEl = form.querySelector('[data-add-to-cart-error]');
  if (!errorEl) {
    errorEl = document.createElement('p');
    errorEl.setAttribute('data-add-to-cart-error', '');
    errorEl.className = 'product-form__error';
    errorEl.hidden = true;
    form.appendChild(errorEl);
  }
  errorEl.hidden = true;

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: payload.id, quantity: Number(payload.quantity) || 1 }),
    });
    const data = await response.json();

    if (!response.ok) {
      errorEl.textContent = data.description || data.message || 'Could not add this item to your cart.';
      errorEl.hidden = false;
      if (submitButton) {
        submitButton.classList.remove('is-loading');
        submitButton.removeAttribute('aria-busy');
        submitButton.disabled = false;
      }
      return;
    }

    await updateCartCount();
    document.dispatchEvent(new CustomEvent('cart:updated'));

    // Brief "Added" confirmation on the button before the drawer opens, so the
    // click is clearly acknowledged even if the drawer takes a moment.
    if (submitButton) {
      submitButton.classList.remove('is-loading');
      submitButton.classList.add('is-added');
      submitButton.removeAttribute('aria-busy');
      if (labelEl) labelEl.textContent = submitButton.dataset.addedLabel || 'Added ✓';
    }

    document.querySelector('[data-cart-open]')?.click();

    // Restore the button shortly after, so it's ready for the next add.
    setTimeout(() => {
      if (submitButton) {
        submitButton.classList.remove('is-added');
        submitButton.disabled = false;
        if (labelEl) labelEl.textContent = originalLabel;
      }
    }, 1400);
  } catch (error) {
    errorEl.textContent = 'Something went wrong. Please try again.';
    errorEl.hidden = false;
    if (submitButton) {
      submitButton.classList.remove('is-loading', 'is-added');
      submitButton.removeAttribute('aria-busy');
      submitButton.disabled = false;
      if (labelEl) labelEl.textContent = originalLabel;
    }
  }
});

/* Quantity changes in the cart.

   Previously the +/- handler set input.value and then dispatched a synthetic
   `change` event, while a separate `change` listener sent the request. In some
   browsers the programmatic value change fires a native `change` too, so the
   request went out twice and a single click moved the quantity by two. Rapid
   clicks also fired one request each, and out-of-order responses made the
   quantity jump back.

   Now the input updates immediately (so the UI feels instant) and a single
   debounced request per line is sent with the final value. */

function queueQuantityChange(key, quantity) {
  if (!key) return;

  // Mark every row for this line as busy so a spinner shows on its total
  // while the request is in flight. Cleared when the section re-renders.
  document
    .querySelectorAll(`[data-cart-item][data-line-item-key="${key}"]`)
    .forEach((row) => row.setAttribute('data-updating', 'true'));

  clearTimeout(cartQuantityTimers.get(key));
  const timer = setTimeout(() => {
    cartQuantityTimers.delete(key);
    cartRequest(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
      id: key,
      quantity: quantity,
    });
  }, 250);

  cartQuantityTimers.set(key, timer);
}

document.addEventListener('click', (event) => {
  const increase = event.target.closest('[data-quantity-increase]');
  const decrease = event.target.closest('[data-quantity-decrease]');
  if (!increase && !decrease) return;

  const wrapper = (increase || decrease).closest('[data-quantity-selector]');
  const input = wrapper?.querySelector('[data-cart-quantity-input]');
  if (!input) return; // product-page selector is handled by product.js

  event.preventDefault();

  const min = Number(input.min) || 0;
  const current = Number(input.value) || 0;
  const next = increase ? current + 1 : Math.max(min, current - 1);
  if (next === current) return;

  // Paint the new value straight away; the request follows once clicking stops.
  input.value = next;
  queueQuantityChange(input.dataset.lineItemKey, next);
});

// Typing directly in the field.
document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-cart-quantity-input]');
  if (!input) return;
  const quantity = Math.max(Number(input.min) || 0, Number(input.value) || 0);
  input.value = quantity;
  queueQuantityChange(input.dataset.lineItemKey, quantity);
});

document.addEventListener('click', (event) => {
  const removeButton = event.target.closest('[data-cart-remove]');
  if (!removeButton) return;
  event.preventDefault();
  const key = removeButton.dataset.lineItemKey;
  cartRequest('/cart/change.js', { id: key, quantity: 0 });
});

// Pre-add quantity stepper on product cards (distinct from cart line-item steppers).
// Uses [data-card-qty-*] hooks so it never collides with the cart drawer stepper.
document.addEventListener('click', (event) => {
  const increase = event.target.closest('[data-card-qty-increase]');
  const decrease = event.target.closest('[data-card-qty-decrease]');
  if (!increase && !decrease) return;

  const wrapper = (increase || decrease).closest('[data-card-qty]');
  const input = wrapper?.querySelector('[data-card-qty-input]');
  if (!input) return;

  const min = Number(input.min) || 1;
  const current = Number(input.value) || min;
  input.value = increase ? current + 1 : Math.max(min, current - 1);
});

/* ---------------------------------------------------------------------------
   Cart page + drawer enhancements: discount code, shipping estimator, timer.
   All delegated so they survive the drawer's innerHTML refresh.
--------------------------------------------------------------------------- */

// ---- Discount code ----
// Shopify has no Ajax endpoint for applying a discount to the cart, so this
// navigates to /discount/{code}, which applies it and redirects back.
document.addEventListener('click', async (event) => {
  const applyButton = event.target.closest('[data-cart-discount-apply]');
  if (!applyButton) return;

  const wrapper = applyButton.closest('[data-cart-discount]');
  const input = wrapper?.querySelector('[data-cart-discount-input]');
  const message = wrapper?.querySelector('[data-cart-discount-message]');
  const code = (input?.value || '').trim();

  if (!code) {
    if (message) message.textContent = 'Enter a discount code.';
    input?.focus();
    return;
  }

  applyButton.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code }),
    });
    const data = await response.json();

    // Shopify accepts the request even when a code is invalid, so verify the
    // code actually landed rather than trusting the 200 response.
    const applied = (data.cart_level_discount_applications || []).some(
      (d) => (d.title || '').toLowerCase() === code.toLowerCase()
    );

    if (!applied && data.total_discount === 0) {
      if (message) message.textContent = "That code is not valid for this cart.";
      return;
    }

    if (input) input.value = '';
    updateCartCount();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
  } catch (error) {
    if (message) message.textContent = 'Could not apply that code. Try again.';
  } finally {
    applyButton.removeAttribute('aria-busy');
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter') return;
  const input = event.target.closest('[data-cart-discount-input]');
  if (!input) return;
  event.preventDefault();
  input.closest('[data-cart-discount]')?.querySelector('[data-cart-discount-apply]')?.click();
});

// ---- Shipping estimator ----
document.addEventListener('click', async (event) => {
  const submit = event.target.closest('[data-estimator-submit]');
  if (!submit) return;

  const root = submit.closest('[data-cart-estimator]');
  const results = root.querySelector('[data-estimator-results]');
  const country = root.querySelector('[data-estimator-country]')?.value || '';
  const province = root.querySelector('[data-estimator-province]')?.value || '';
  const zip = root.querySelector('[data-estimator-zip]')?.value || '';

  results.innerHTML = '';
  submit.setAttribute('aria-busy', 'true');

  const params = new URLSearchParams({
    'shipping_address[country]': country,
    'shipping_address[province]': province,
    'shipping_address[zip]': zip,
  });

  try {
    const response = await fetch('/cart/shipping_rates.json?' + params.toString());
    const data = await response.json();

    if (!response.ok || data.errors) {
      const detail = data.errors
        ? Object.values(data.errors).flat().join(' ')
        : 'Could not fetch rates.';
      results.innerHTML = '<p class="cart-estimator__error"></p>';
      results.querySelector('p').textContent = detail;
      return;
    }

    const rates = data.shipping_rates || [];
    if (rates.length === 0) {
      results.innerHTML = '<p class="cart-estimator__error">No shipping available to this destination.</p>';
      return;
    }

    results.innerHTML = rates
      .map((rate) => {
        const name = document.createElement('span');
        name.textContent = rate.name;
        const price = document.createElement('span');
        price.className = 'cart-estimator__rate-price';
        price.textContent = rate.price === '0.00' ? 'Free' : rate.price;
        return '<div class="cart-estimator__rate">' + name.outerHTML + price.outerHTML + '</div>';
      })
      .join('');
  } catch (error) {
    results.innerHTML = '<p class="cart-estimator__error">Could not fetch rates. Try again.</p>';
  } finally {
    submit.removeAttribute('aria-busy');
  }
});

// Province field only appears for countries that have them.
document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-estimator-country]');
  if (!select) return;

  const root = select.closest('[data-cart-estimator]');
  const wrap = root.querySelector('[data-estimator-province-wrap]');
  const provinceSelect = root.querySelector('[data-estimator-province]');
  const option = select.options[select.selectedIndex];

  let provinces = [];
  try {
    provinces = JSON.parse(option.getAttribute('data-provinces') || '[]');
  } catch (e) {
    provinces = [];
  }

  if (provinces.length === 0) {
    wrap.hidden = true;
    provinceSelect.innerHTML = '';
    return;
  }

  provinceSelect.innerHTML = provinces
    .map((p) => '<option value="' + p[0] + '">' + p[1] + '</option>')
    .join('');
  wrap.hidden = false;
});

// ---- Cart hold countdown ----
// Purely a reminder that the cart is temporary. It does not reserve stock,
// and the default copy does not claim it does.
function initCartTimers() {
  document.querySelectorAll('[data-cart-timer]').forEach((timer) => {
    if (timer.dataset.timerRunning === 'true') return;
    timer.dataset.timerRunning = 'true';

    const display = timer.querySelector('[data-cart-timer-display]');
    const minutes = parseInt(timer.dataset.minutes, 10) || 10;

    // Anchor to a stored start so the countdown survives navigation rather
    // than restarting on every page load.
    let start;
    try {
      start = Number(sessionStorage.getItem('neroli-cart-timer'));
      if (!start) {
        start = Date.now();
        sessionStorage.setItem('neroli-cart-timer', String(start));
      }
    } catch (e) {
      start = Date.now();
    }

    const endsAt = start + minutes * 60 * 1000;

    const tick = () => {
      const remaining = endsAt - Date.now();
      if (remaining <= 0) {
        timer.hidden = true;
        clearInterval(interval);
        try { sessionStorage.removeItem('neroli-cart-timer'); } catch (e) {}
        return;
      }
      const mins = Math.floor(remaining / 60000);
      const secs = Math.floor((remaining % 60000) / 1000);
      if (display) display.textContent = mins + ':' + String(secs).padStart(2, '0');
    };

    tick();
    const interval = setInterval(tick, 1000);
  });
}

document.addEventListener('DOMContentLoaded', initCartTimers);
document.addEventListener('cart:updated', () => setTimeout(initCartTimers, 50));

// ---- Share cart ----
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-cart-share-copy]');
  if (!button) return;

  const root = button.closest('[data-cart-share]');
  const input = root?.querySelector('[data-cart-share-input]');
  const message = root?.querySelector('[data-cart-share-message]');
  if (!input) return;

  try {
    await navigator.clipboard.writeText(input.value);
    if (message) message.textContent = 'Link copied.';
  } catch (e) {
    // Clipboard API needs a secure context; fall back to selecting the text.
    input.select();
    input.setSelectionRange(0, input.value.length);
    if (message) message.textContent = 'Press Ctrl/Cmd + C to copy.';
  }

  setTimeout(() => { if (message) message.textContent = ''; }, 3000);
});

// ---- Cart note ----
// The note must be written to the cart, not merely submitted with the form:
// checkout buttons navigate away without posting the note, and the drawer
// re-renders on every cart update, which would discard an untyped-but-unsaved
// value. Saving on input (debounced) and on blur keeps it durable.
(function () {
  let noteTimer;

  async function saveNote(value) {
    try {
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: value }),
      });
    } catch (e) {
      // Non-fatal: the note simply isn't persisted if the request fails.
    }
  }

  document.addEventListener('input', (event) => {
    const field = event.target.closest('[data-cart-note]');
    if (!field) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => saveNote(field.value), 500);
  });

  document.addEventListener('blur', (event) => {
    const field = event.target.closest?.('[data-cart-note]');
    if (!field) return;
    clearTimeout(noteTimer);
    saveNote(field.value);
  }, true);
})();

/* ---------------------------------------------------------------------------
   Cart page live updates.

   cartRequest fires `cart:updated`, but only <cart-drawer> was listening. On
   /cart the item was genuinely removed server-side while the page kept showing
   the old markup, so remove and quantity changes appeared to do nothing.
   This re-renders the cart section in place using the Section Rendering API.
--------------------------------------------------------------------------- */
(function () {
  const pageRoot = document.querySelector('.main-cart');
  if (!pageRoot) return;

  const sectionWrapper = pageRoot.closest('[id^="shopify-section-"]');
  const sectionId = sectionWrapper
    ? sectionWrapper.id.replace('shopify-section-', '')
    : 'main';

  let refreshing = false;

  async function refreshCartPage() {
    if (refreshing) return;
    // Don't swap the markup while a quantity change is still settling.
    if (cartQuantityTimers.size > 0) return;
    refreshing = true;

    // Preserve an in-progress note across the swap.
    const liveNote = document.querySelector('.main-cart [data-cart-note]');
    const noteValue = liveNote ? liveNote.value : null;
    const noteWasOpen = liveNote ? !!liveNote.closest('details[open]') : false;

    try {
      // Must request the cart URL, not the site root, or the section renders
      // against the wrong template.
      const cartUrl = `${window.Shopify?.routes?.root || '/'}cart`;
      const response = await fetch(`${cartUrl}?section_id=${sectionId}`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const fresh = doc.querySelector('.main-cart');
      const current = document.querySelector('.main-cart');

      if (fresh && current) {
        current.innerHTML = fresh.innerHTML;

        if (noteValue !== null) {
          const restored = current.querySelector('[data-cart-note]');
          if (restored) {
            restored.value = noteValue;
            const details = restored.closest('details');
            if (details && noteWasOpen) details.open = true;
          }
        }
      }
    } catch (error) {
      window.location.reload();
    } finally {
      refreshing = false;
    }
  }

  document.addEventListener('cart:updated', refreshCartPage);
})();

/* ---------------------------------------------------------------------------
   Cart utility row: note / share panels, and empty-cart confirmation.
--------------------------------------------------------------------------- */

// Toggle the note and share panels; only one open at a time.
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-cart-panel-toggle]');
  if (!toggle) return;

  const name = toggle.dataset.cartPanelToggle;
  const scope = toggle.closest('.cart-drawer__footer, .main-cart__summary-inner') || document;
  const panel = scope.querySelector(`[data-cart-panel="${name}"]`);
  if (!panel) return;

  const willOpen = panel.hidden;

  scope.querySelectorAll('[data-cart-panel]').forEach((p) => { p.hidden = true; });
  scope.querySelectorAll('[data-cart-panel-toggle]').forEach((t) => {
    t.setAttribute('aria-expanded', 'false');
  });

  panel.hidden = !willOpen;
  toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');

  if (willOpen) {
    panel.querySelector('textarea, input')?.focus();
  }
});

// ---- Empty cart, with confirmation ----
function cartEmptyDialog(trigger) {
  const scope = trigger.closest('cart-drawer, .main-cart') || document;
  return scope.querySelector('[data-cart-empty-dialog]');
}

document.addEventListener('click', (event) => {
  const open = event.target.closest('[data-cart-empty-open]');
  if (open) {
    const dialog = cartEmptyDialog(open);
    if (dialog) {
      dialog.hidden = false;
      dialog.querySelector('[data-cart-empty-confirm]')?.focus();
    }
    return;
  }

  const cancel = event.target.closest('[data-cart-empty-cancel]');
  if (cancel) {
    const dialog = cancel.closest('[data-cart-empty-dialog]');
    if (dialog) dialog.hidden = true;
    return;
  }

  const confirm = event.target.closest('[data-cart-empty-confirm]');
  if (confirm) {
    const dialog = confirm.closest('[data-cart-empty-dialog]');
    confirm.setAttribute('aria-busy', 'true');

    // /cart/clear.js removes every line in one request.
    fetch(`${window.Shopify?.routes?.root || '/'}cart/clear.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    })
      .then((response) => response.json())
      .then((data) => {
        if (dialog) dialog.hidden = true;
        updateCartCount();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      })
      .catch(() => {
        window.location.reload();
      })
      .finally(() => {
        confirm.removeAttribute('aria-busy');
      });
  }
});

// Escape closes the confirmation.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('[data-cart-empty-dialog]:not([hidden])').forEach((d) => {
    d.hidden = true;
  });
});

// Close button inside a utility panel (note / share sheet).
document.addEventListener('click', (event) => {
  const close = event.target.closest('[data-cart-panel-close]');
  if (!close) return;
  const panel = close.closest('[data-cart-panel]');
  if (!panel) return;
  panel.hidden = true;
  const scope = panel.closest('.cart-drawer__footer, .main-cart__summary-inner') || document;
  scope.querySelectorAll('[data-cart-panel-toggle]').forEach((t) => {
    t.setAttribute('aria-expanded', 'false');
  });
});

// ---- Remove an applied discount ----
// /cart/update.js accepts a `discount` parameter. Passing an empty string
// clears the applied codes without leaving the page.
document.addEventListener('click', async (event) => {
  const remove = event.target.closest('[data-cart-discount-remove]');
  if (!remove) return;

  remove.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: '' }),
    });
    const data = await response.json();
    updateCartCount();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
  } catch (error) {
    window.location.reload();
  } finally {
    remove.removeAttribute('aria-busy');
  }
});

// ---- Explicit note save ----
// The note already auto-saves while typing, but without visible confirmation
// people reasonably assume it hasn't been stored. This makes it explicit.
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-cart-note-save]');
  if (!button) return;

  const panel = button.closest('[data-cart-panel]');
  const field = panel?.querySelector('[data-cart-note]');
  const status = panel?.querySelector('[data-cart-note-status]');
  if (!field) return;

  button.setAttribute('aria-busy', 'true');
  if (status) status.textContent = '';

  try {
    await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: field.value }),
    });
    if (status) status.textContent = 'Note saved.';
    setTimeout(() => {
      if (panel) panel.hidden = true;
      const scope = panel.closest('.cart-drawer__footer, .main-cart__summary-inner') || document;
      scope.querySelectorAll('[data-cart-panel-toggle]').forEach((t) => {
        t.setAttribute('aria-expanded', 'false');
      });
      if (status) status.textContent = '';
    }, 900);
  } catch (error) {
    if (status) status.textContent = "Couldn't save the note. Try again.";
  } finally {
    button.removeAttribute('aria-busy');
  }
});

/* ---------------------------------------------------------------------------
   Tab title cart reminder.

   When items are in the cart and the customer switches to another tab, the tab
   title changes to remind them (e.g. "(2) items waiting in your cart"). The
   browser shows this same text as the tab's hover tooltip — there's no separate
   API for the tooltip, so the title is what drives both. On returning to the
   tab, the original title is restored.
--------------------------------------------------------------------------- */
(function cartTabReminder() {
  const originalTitle = document.title;
  let itemCount = 0;

  // ---- Favicon badge ----
  // Draw the cart count as a small badge over the site favicon. Falls back
  // silently if there's no favicon or the canvas can't be drawn.
  const faviconLink =
    document.querySelector('link[rel="icon"]') ||
    document.querySelector('link[rel="shortcut icon"]');
  const originalFavicon = faviconLink ? faviconLink.href : null;
  let baseIconImage = null;

  if (faviconLink && originalFavicon) {
    const loadIcon = (useCors) => {
      const img = new Image();
      if (useCors) img.crossOrigin = 'anonymous';
      img.onload = () => { baseIconImage = img; updateFavicon(); };
      img.onerror = () => {
        // A cached non-CORS response can block the anonymous request; retry
        // once without crossOrigin so the badge still draws.
        if (useCors) loadIcon(false);
      };
      // Cache-bust so a previously cached (possibly non-CORS) copy isn't reused.
      img.src = originalFavicon + (originalFavicon.includes('?') ? '&' : '?') + 'faviconbadge=1';
    };
    loadIcon(true);
  }

  function updateFavicon() {
    if (!faviconLink) return;
    // Badge only shows while the customer is away on another tab. When the tab
    // is visible, or the cart is empty, restore the clean icon.
    if (itemCount <= 0 || !document.hidden) {
      if (originalFavicon) faviconLink.href = originalFavicon;
      return;
    }
    if (!baseIconImage) return;

    try {
      // Render at a high resolution so the badge stays crisp when the browser
      // scales it down to favicon size.
      const size = 128;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(baseIconImage, 0, 0, size, size);

      // Larger badge that fills most of the icon so the number is legible even
      // at 16px. Positioned bottom-right.
      const r = size * 0.46;
      const cx = size - r;
      const cy = size - r;

      // White ring behind the badge lifts it off the icon for contrast.
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();

      ctx.beginPath();
      ctx.arc(cx, cy, r * 0.86, 0, Math.PI * 2);
      ctx.fillStyle = '#e2483d';
      ctx.fill();

      // Count text, sized to fill the badge.
      ctx.fillStyle = '#ffffff';
      const label = itemCount > 9 ? '9+' : String(itemCount);
      const fontSize = label.length > 1 ? size * 0.5 : size * 0.62;
      ctx.font = `700 ${fontSize}px -apple-system, BlinkMacSystemFont, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, cx, cy + size * 0.02);

      faviconLink.href = canvas.toDataURL('image/png');
    } catch (e) {
      // Cross-origin favicon or unsupported canvas — leave the icon as-is.
    }
  }

  // Read the count already rendered on the page so we start correct without a
  // fetch. Falls back to a /cart.js read if the badge isn't present.
  function readInitialCount() {
    const badge = document.querySelector('[data-cart-count]');
    if (badge && badge.textContent.trim() !== '') {
      const n = parseInt(badge.textContent.trim(), 10);
      if (!Number.isNaN(n)) { itemCount = n; applyTitle(); updateFavicon(); return; }
    }
    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
      .then((r) => r.json())
      .then((cart) => { itemCount = cart.item_count || 0; applyTitle(); updateFavicon(); })
      .catch(() => {});
  }

  function reminderText() {
    if (itemCount <= 0) return originalTitle;
    const noun = itemCount === 1 ? 'item' : 'items';
    return `(${itemCount}) ${noun} waiting in your cart`;
  }

  // Title reminder shows only while the customer is away on another tab, then
  // restores the clean title when they return. The browser also surfaces this
  // as the tab's hover tooltip.
  function applyTitle() {
    if (itemCount > 0 && document.hidden) {
      document.title = reminderText();
    } else {
      document.title = originalTitle;
    }
  }

  document.addEventListener('visibilitychange', () => {
    applyTitle();
    updateFavicon();
  });

  document.addEventListener('cart:updated', (event) => {
    const cart = event.detail;
    if (cart && typeof cart.item_count === 'number') {
      itemCount = cart.item_count;
      applyTitle();
      updateFavicon();
    } else {
      // Some dispatches carry no detail; read the fresh count.
      fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
        .then((r) => r.json())
        .then((c) => { itemCount = c.item_count || 0; applyTitle(); updateFavicon(); })
        .catch(() => {});
    }
  });

  readInitialCount();
})();
